import jsPDF from "jspdf";
import { Document, Packer, Paragraph, TextRun, AlignmentType } from "docx";
import { saveAs } from "file-saver";

function hexToRgb(hex) {
  const h = (hex || "#0f172a").replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function todayLong() {
  return new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function recipientBlock(recipient) {
  const lines = [];
  if (recipient?.name) lines.push(recipient.name);
  if (recipient?.title) lines.push(recipient.title);
  if (recipient?.company) lines.push(recipient.company);
  if (recipient?.address) lines.push(...recipient.address.split("\n").filter(Boolean));
  return lines;
}

export async function exportCoverLetterToPDF(letter, filename = "cover-letter.pdf") {
  const pi = letter.personalInfo || {};
  const accent = hexToRgb(letter.accentColor || "#0f172a");
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 64;
  let y = margin;

  // Sender name + contact
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.setTextColor(accent.r, accent.g, accent.b);
  if (pi.fullName) {
    pdf.text(pi.fullName, margin, y);
    y += 22;
  }
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(71, 85, 105);
  const contact = [pi.email, pi.phone, pi.location].filter(Boolean).join("  •  ");
  if (contact) {
    pdf.text(contact, margin, y);
    y += 14;
  }

  // Accent rule
  pdf.setDrawColor(accent.r, accent.g, accent.b);
  pdf.setLineWidth(1);
  pdf.line(margin, y + 6, pageW - margin, y + 6);
  y += 28;

  // Date
  pdf.setFontSize(10.5);
  pdf.setTextColor(51, 65, 85);
  pdf.text(todayLong(), margin, y);
  y += 22;

  // Recipient
  const recipLines = recipientBlock(letter.recipient);
  recipLines.forEach((line) => {
    pdf.text(line, margin, y);
    y += 13;
  });
  if (recipLines.length) y += 8;

  // Salutation
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(15, 23, 42);
  const salutation = letter.recipient?.name ? `Dear ${letter.recipient.name.split(" ")[0]},` : "Dear Hiring Manager,";
  pdf.text(salutation, margin, y);
  y += 18;

  // Body — split into paragraphs by blank lines
  const body = (letter.body || "").trim();
  const paragraphs = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  pdf.setFontSize(11);
  pdf.setTextColor(30, 41, 59);
  paragraphs.forEach((para) => {
    const wrapped = pdf.splitTextToSize(para, pageW - margin * 2);
    if (y + wrapped.length * 15 > pageH - margin) {
      pdf.addPage();
      y = margin;
    }
    pdf.text(wrapped, margin, y);
    y += wrapped.length * 15 + 8;
  });

  // Closing
  y += 6;
  if (y > pageH - margin - 60) {
    pdf.addPage();
    y = margin;
  }
  pdf.text("Sincerely,", margin, y);
  y += 28;
  pdf.setFont("helvetica", "bold");
  pdf.text(pi.fullName || "", margin, y);

  pdf.save(filename);
}

function p(text, opts = {}) {
  return new Paragraph({
    children: [new TextRun({ text: text || "", bold: opts.bold, size: opts.size, color: opts.color, italics: opts.italics })],
    spacing: { after: opts.after ?? 120 },
    alignment: opts.alignment,
  });
}

export async function exportCoverLetterToDOCX(letter, filename = "cover-letter.docx") {
  const pi = letter.personalInfo || {};
  const children = [];

  if (pi.fullName) {
    children.push(p(pi.fullName, { bold: true, size: 36, color: "0F172A", after: 60 }));
  }
  const contact = [pi.email, pi.phone, pi.location].filter(Boolean).join("  •  ");
  if (contact) {
    children.push(p(contact, { size: 20, color: "475569", after: 240 }));
  }
  children.push(p(todayLong(), { size: 22, color: "334155", after: 200 }));

  recipientBlock(letter.recipient).forEach((line) => {
    children.push(p(line, { size: 22, color: "0F172A", after: 60 }));
  });
  if (recipientBlock(letter.recipient).length) {
    children.push(p("", { size: 20, after: 120 }));
  }

  const salutation = letter.recipient?.name ? `Dear ${letter.recipient.name.split(" ")[0]},` : "Dear Hiring Manager,";
  children.push(p(salutation, { size: 22, after: 160 }));

  const body = (letter.body || "").trim();
  const paragraphs = body.split(/\n{2,}/).map((para) => para.trim()).filter(Boolean);
  paragraphs.forEach((para) => {
    children.push(p(para, { size: 22, after: 160 }));
  });

  children.push(p("Sincerely,", { size: 22, after: 240 }));
  children.push(p(pi.fullName || "", { bold: true, size: 22 }));

  const doc = new Document({ sections: [{ properties: {}, children }] });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, filename);
}
